import { useEffect, useState } from 'react';
import { Card, Container, Flex, Heading, HStack, Icon, Spacer, Spinner, Stack, Text, Button } from '@chakra-ui/react';
import { BookOpenIcon, ChartBarIcon, CommandLineIcon, PlusIcon } from '@heroicons/react/24/outline';
import { NavLink, useParams } from 'react-router-dom';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import useApiKeysStore from '@/utils/use-api-keys';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import ApiKeysList from '@/components/developer/api-keys-list';
import CreateApiKeyDialog from '@/components/developer/create-api-key-dialog';
import RevokeConfirmDialog from '@/components/developer/revoke-confirm-dialog';
import RenameApiKeyDialog from '@/components/developer/rename-api-key-dialog';
import RotateApiKeyDialog from '@/components/developer/rotate-api-key-dialog';
import ApiKeyAuditDialog from '@/components/developer/api-key-audit-dialog';
import { ApiKey } from '@/lib/types';

export default function DeveloperKeys() {
  const { workspaceId } = useParams();
  const isAdmin = useIsInAdminGroup();
  const apiKeys = useApiKeysStore((s) => s.apiKeys);
  const loading = useApiKeysStore((s) => s.loading);
  const error = useApiKeysStore((s) => s.error);
  const loadApiKeys = useApiKeysStore((s) => s.loadApiKeys);
  const createModal = useModalToggle();
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [renameTarget, setRenameTarget] = useState<ApiKey | null>(null);
  const [rotateTarget, setRotateTarget] = useState<ApiKey | null>(null);
  const [auditTarget, setAuditTarget] = useState<ApiKey | null>(null);

  useEffect(() => {
    if (workspaceId && isAdmin) {
      loadApiKeys(workspaceId);
    }
  }, [workspaceId, isAdmin, loadApiKeys]);

  if (!isAdmin) {
    return (
      <WorkspaceLayout>
        <Card.Root mx='auto' width={{ md: '90%' }} maxW='4xl'>
          <Card.Body>
            <Stack textAlign='center' gap={3} py={10}>
              <Heading size='lg'>Workspace admin access required</Heading>
              <Text color='fg.muted'>
                Only workspace admins can view or manage API keys.
              </Text>
            </Stack>
          </Card.Body>
        </Card.Root>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <Card.Root minH='100vh' mx='auto' width={{ md: '90%' }} maxW='6xl'>
        <Card.Header>
          <Container maxW='5xl'>
            <Flex alignItems='center' mb={4}>
              <Stack direction='row' align='center'>
                <Icon as={CommandLineIcon} w={7} h={7} color='brand.solid' />
                <Heading size='3xl'>API keys</Heading>
              </Stack>
              <Spacer />
              <HStack gap={2}>
                <Button variant='outline' asChild>
                  <NavLink to={`/${workspaceId}/developer/analytics`}>
                    <Icon as={ChartBarIcon} w={4} h={4} />
                    Analytics
                  </NavLink>
                </Button>
                <Button variant='outline' asChild>
                  <NavLink to='/developers/docs' target='_blank' rel='noopener noreferrer'>
                    <Icon as={BookOpenIcon} w={4} h={4} />
                    View API docs
                  </NavLink>
                </Button>
                <Button colorPalette='brand' onClick={createModal.open}>
                  <Icon as={PlusIcon} w={4} h={4} />
                  Create API key
                </Button>
              </HStack>
            </Flex>
            <Text color='fg.muted' fontSize='sm'>
              API keys grant programmatic access to this workspace&apos;s data through the public
              API. Scope each key to the minimum it needs and rotate them periodically. Anyone
              with a key can access whatever its scopes allow.
            </Text>
          </Container>
        </Card.Header>
        <Card.Body>
          <Container maxW='5xl'>
            {loading && (
              <Flex justify='center' py={10}>
                <Spinner />
              </Flex>
            )}
            {!loading && error && (
              <Stack textAlign='center' gap={2} py={6}>
                <Text color='danger'>{error}</Text>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => workspaceId && loadApiKeys(workspaceId)}
                >
                  Try again
                </Button>
              </Stack>
            )}
            {!loading && !error && (
              <ApiKeysList
                apiKeys={apiKeys}
                onRename={setRenameTarget}
                onRotate={setRotateTarget}
                onViewAudit={setAuditTarget}
                onRevoke={setRevokeTarget}
              />
            )}
          </Container>
        </Card.Body>
      </Card.Root>
      <CreateApiKeyDialog {...createModal.props} onClose={createModal.close} />
      <RevokeConfirmDialog
        isOpen={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        apiKey={revokeTarget}
      />
      <RenameApiKeyDialog
        isOpen={renameTarget !== null}
        onClose={() => setRenameTarget(null)}
        apiKey={renameTarget}
      />
      <RotateApiKeyDialog
        isOpen={rotateTarget !== null}
        onClose={() => setRotateTarget(null)}
        apiKey={rotateTarget}
      />
      <ApiKeyAuditDialog
        isOpen={auditTarget !== null}
        onClose={() => setAuditTarget(null)}
        apiKey={auditTarget}
      />
    </WorkspaceLayout>
  );
}
